import { useCallback, useMemo, useRef, useState, type ComponentType } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DropdownMenu, type MenuSection } from '@features/shared/components/DropdownMenu'
import { IconButton } from '@features/shared/components/IconButton'
import { POPOVER_MENU_ICON_SIZE_PX } from '@features/shared/components/PopoverMenu'
import { HeartIcon, HomeIcon, KebabIcon, type IconProps } from '@features/shared/icons/icons'
import { useAppNavigation } from '@features/navigation/hooks/useAppNavigation'
import { useTableOrganization } from '../hooks'
import { favoriteTargetDescription } from '../logic/favoriteTargetDescription'
import { MenuDescription } from './MenuDescription'
import type { SidebarFavorite } from '../api/contracts'
import {
  useFavoriteChanges,
  useFavoriteDescriptions,
  useFavoritesQuery,
  useFavoritesSort,
  useIsHomePage,
  useSetHomePage,
} from '../hooks/useFavorites'
import {
  FAVORITE_NAME_SEPARATOR,
  isPathWithin,
  sortFavorites,
  type FavoriteDescription,
} from '../logic/favorites'
import {
  SIDEBAR_DRAG_MEASURING,
  listDragCollision,
  sidebarDragAnnouncements,
  sidebarListDragModifiers,
} from '../logic/dragInList'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import {
  FavoriteToggle,
  REMOVE_FROM_FAVORITES_LABEL,
  SET_AS_HOME_PAGE_LABEL,
} from './FavoriteToggle'
import SidebarDragChip from './SidebarDragChip'
import { DRAG_SWAPPED_ICON_CLASS, LeadingDragHandle } from './LeadingDragHandle'
import {
  MENU_ICON_SLOT_CLASS,
  MENU_ICON_SLOTS_CLASS,
  MENU_ROW_END_PAD_CLASS,
  menuIconSlotClass,
} from './menuIconSlots'
import FadeTruncatedText from '@features/shared/components/FadeTruncatedText'

// A row's icon, its area's: its size, and where it starts (the row's left
// padding). Under Custom sort the drag handle takes its place on hover.
const FAVORITE_ICON_SIZE = 15
const FAVORITE_ICON_LEFT = 8
const sortableId = (favorite: SidebarFavorite) => `favorite-${favorite.path}`

type FavoriteIcon = ComponentType<IconProps>

interface FavoriteRowProps {
  favorite: SidebarFavorite
  description: FavoriteDescription
  /** Its area's icon. */
  icon: FavoriteIcon
  isActive: boolean
  /** Custom sort: the row can be dragged into a new place. */
  reorderable: boolean
  onSelect?: () => void
  /** What it points to, described (favoriteTargetDescription); its info mark. */
  targetDescription?: string | null
}

/**
 * A favorite's trail: the steps before its own page muted, its own page darkest,
 * as breadcrumbs read. When the row is tight, the steps before it
 * give way, so its own page stays whole unless it alone is too long.
 */
export function FavoriteTrail({
  parts,
  isActive,
}: {
  parts: readonly string[]
  isActive: boolean
}) {
  const page = parts[parts.length - 1] ?? ''
  const before = parts.slice(0, -1)
  // On the selected row the steps keep its blue, a little fainter.
  const beforeClass = isActive ? 'opacity-70' : 'text-[color:var(--sidebar-submenu-text-muted)]'
  // Its own page reads darkest, as the menu's title does; selected, in the row's blue.
  const pageClass = isActive ? '' : 'text-[color:var(--sidebar-submenu-text-default)]'
  return (
    <span className="flex min-w-0 items-center">
      {before.length > 0 && (
        <>
          <FadeTruncatedText className={beforeClass}>
            {before.join(FAVORITE_NAME_SEPARATOR)}
          </FadeTruncatedText>
          <span className={`shrink-0 whitespace-pre ${beforeClass}`}>
            {FAVORITE_NAME_SEPARATOR}
          </span>
        </>
      )}
      {/* Never shrunk (a sliver would be all fade), only capped at the row. */}
      <FadeTruncatedText className={`max-w-full shrink-0 ${pageClass}`}>{page}</FadeTruncatedText>
    </span>
  )
}

function FavoriteRow({
  favorite,
  description,
  icon: Icon,
  isActive,
  reorderable,
  onSelect,
  targetDescription = null,
}: FavoriteRowProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const rowRef = useRef<HTMLDivElement | null>(null)
  const { remove } = useFavoriteChanges()
  const isHomePage = useIsHomePage(favorite.path)
  const setHomePage = useSetHomePage()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId(favorite),
    data: { type: 'favorite', path: favorite.path },
    disabled: !reorderable,
  })
  const setRowRef = useCallback(
    (node: HTMLDivElement | null) => {
      rowRef.current = node
      setNodeRef(node)
    },
    [setNodeRef]
  )

  const sections: MenuSection[] = [
    {
      items: [
        {
          type: 'action',
          id: 'remove-favorite',
          label: REMOVE_FROM_FAVORITES_LABEL,
          icon: <HeartIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => remove(favorite.path),
        },
        {
          type: 'action',
          id: 'set-home-page',
          label: SET_AS_HOME_PAGE_LABEL,
          icon: <HomeIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          omitted: isHomePage,
          onClick: () => setHomePage(favorite.path, description.name),
        },
      ],
    },
  ]

  return (
    <div
      ref={setRowRef}
      style={{
        // While dragging, the in-list row is hidden; the overlay is the moving copy.
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
      className={`group relative flex h-[var(--sidebar-tile-size)] w-full min-w-0 items-center rounded-md pl-2 ${MENU_ROW_END_PAD_CLASS} text-sm ${
        // Selected and hovered like the rail's items (the sidebar-item tokens).
        isActive
          ? 'bg-[color:var(--sidebar-item-active-bg)] font-medium text-[color:var(--sidebar-item-active-text)] hover:bg-[color:var(--sidebar-item-active-hover-bg)]'
          : 'text-[color:var(--sidebar-submenu-text-data)] hover:bg-[color:var(--sidebar-item-hover-bg)]'
      }`}
    >
      {reorderable && (
        <LeadingDragHandle
          left={FAVORITE_ICON_LEFT}
          size={FAVORITE_ICON_SIZE}
          listeners={listeners}
          attributes={attributes}
        />
      )}
      <Link
        to={favorite.path}
        // The full text of the truncated name, on the link (whose overlay covers
        // the row) so the row's own controls show only their tooltips.
        title={description.name}
        className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left after:absolute after:inset-0 after:rounded-md after:content-['']"
        aria-current={isActive ? 'page' : undefined}
        onClick={(event) => {
          event.stopPropagation()
          if (!event.ctrlKey && !event.metaKey) onSelect?.()
        }}
      >
        <Icon
          size={FAVORITE_ICON_SIZE}
          className={`shrink-0 ${reorderable ? DRAG_SWAPPED_ICON_CLASS : ''}`}
          aria-hidden="true"
        />
        <FavoriteTrail parts={description.parts} isActive={isActive} />
        <MenuDescription description={targetDescription} />
      </Link>
      {/* The row's options menu, then its heart in the last slot (menuIconSlots). */}
      <span
        className={`pointer-events-none relative z-10 ml-auto h-6 pl-2 ${MENU_ICON_SLOTS_CLASS}`}
      >
        <span className={menuIconSlotClass(menuOpen ? 'shown' : 'collapsed')}>
          <DropdownMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            sections={sections}
            align="right"
            placement="right-start"
            anchorElementRef={rowRef}
            className="min-w-52"
            trigger={
              <IconButton
                label="Favorite options"
                className={`pointer-events-auto ${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faintest)] transition-colors hover:text-[color:var(--sidebar-submenu-text-data)]`}
              >
                <KebabIcon size={14} aria-hidden="true" />
              </IconButton>
            }
          />
        </span>
        <span className="pointer-events-auto flex">
          <FavoriteToggle path={favorite.path} variant="row" />
        </span>
      </span>
    </div>
  )
}

/**
 * The Favorites menu beside the rail: the pages the user hearted, each read as
 * its trail from its metadata (Tables › Accounts), as the homepage setting
 * reads an address, in the user's sort (Custom, the order added unless dragged,
 * by default). The names are worked out in the background before the menu
 * opens (useDescribeFavorites), so it never waits on them. A favorite whose
 * page is gone comes off; one whose area is switched off stays hidden until
 * it's back.
 */
export default function FavoritesMenu({ onSelect }: { onSelect?: () => void }) {
  const { data } = useFavoritesQuery()
  // A drop shows its new order at once: the save writes it to the cache a tick
  // later (useFavoriteChanges), and until then the list would draw the old
  // order for a frame, the row jumping back before it settles.
  const [dropped, setDropped] = useState<{
    base: SidebarFavorite[]
    favorites: SidebarFavorite[]
  } | null>(null)
  const favorites =
    dropped && dropped.base === data?.favorites ? dropped.favorites : data?.favorites
  const descriptions = useFavoriteDescriptions()
  const { sort } = useFavoritesSort()
  const { reorder } = useFavoriteChanges()
  const { pathname } = useLocation()
  const { modules } = useAppNavigation()
  const { data: organization } = useTableOrganization()
  const tables = organization?.tables
  const setRowDragActive = useGlobalSidebarStore((s) => s.setRowDragActive)
  const [dragged, setDragged] = useState<SidebarFavorite | null>(null)
  const navRef = useRef<HTMLElement | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  const collisionDetection = useMemo(() => listDragCollision(), [])
  const dragModifiers = useMemo(() => sidebarListDragModifiers(() => navRef.current), [])

  const shown = useMemo(
    () =>
      sortFavorites(favorites ?? [], descriptions, sort).filter((favorite) => {
        const status = descriptions.get(favorite.path)?.status
        // One not named yet (a first visit on this device) holds its place.
        return status === undefined || status === 'ok' || status === 'unknown'
      }),
    [descriptions, favorites, sort]
  )
  // The favorite the open page belongs to; the closest when several hold it.
  const activePath = shown
    .filter((favorite) => isPathWithin(pathname, favorite.path))
    .reduce<
      string | null
    >((closest, favorite) => (closest === null || favorite.path.length > closest.length ? favorite.path : closest), null)

  // A favorite's icon is its area's, as on the rail.
  const iconFor = (description: FavoriteDescription): FavoriteIcon =>
    modules.find((module) => module.pathPrefix === `/${description.area}`)?.icon ?? HeartIcon

  const describeDragId = useCallback(
    (id: UniqueIdentifier) =>
      descriptions.get(String(id).replace(/^favorite-/, ''))?.name ?? 'a favorite',
    [descriptions]
  )
  const accessibility = useMemo(
    () => ({ announcements: sidebarDragAnnouncements(describeDragId) }),
    [describeDragId]
  )

  if (!favorites) return null

  if (shown.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
        <HeartIcon
          size={18}
          className="text-[color:var(--sidebar-submenu-text-faint)]"
          aria-hidden="true"
        />
        <p className="text-sm font-medium text-[color:var(--sidebar-submenu-text-default)]">
          No favorites yet
        </p>
        <p className="text-xs text-[color:var(--sidebar-submenu-text-faint)]">
          Click the heart beside a page's title, or on a table or view, to add it here.
        </p>
      </div>
    )
  }

  const reorderable = sort === 'custom'
  const finishDrag = () => {
    setDragged(null)
    setRowDragActive(false)
  }
  const handleDragStart = (event: DragStartEvent) => {
    const path = event.active.data.current?.path as string | undefined
    setDragged(favorites.find((favorite) => favorite.path === path) ?? null)
    setRowDragActive(true)
  }
  const handleDragEnd = (event: DragEndEvent) => {
    finishDrag()
    const from = favorites.findIndex(
      (favorite) => favorite.path === event.active.data.current?.path
    )
    const to = favorites.findIndex((favorite) => favorite.path === event.over?.data.current?.path)
    if (from < 0 || to < 0 || from === to) return
    const next = arrayMove(favorites, from, to)
    if (data?.favorites) setDropped({ base: data.favorites, favorites: next })
    reorder(next.map((favorite) => favorite.path))
  }
  const draggedDescription = dragged ? descriptions.get(dragged.path) : undefined

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={dragModifiers}
      measuring={SIDEBAR_DRAG_MEASURING}
      accessibility={accessibility}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={finishDrag}
    >
      <nav
        ref={navRef}
        aria-label="Favorites"
        className="min-w-full space-y-[var(--sidebar-item-inset)]"
      >
        <SortableContext
          items={shown.filter((favorite) => descriptions.has(favorite.path)).map(sortableId)}
          strategy={verticalListSortingStrategy}
        >
          {shown.map((favorite) => {
            const description = descriptions.get(favorite.path)
            if (!description) {
              return (
                <div
                  key={favorite.path}
                  aria-hidden="true"
                  className="h-[var(--sidebar-tile-size)] animate-pulse rounded-md bg-muted"
                />
              )
            }
            return (
              <FavoriteRow
                key={favorite.path}
                favorite={favorite}
                description={description}
                icon={iconFor(description)}
                isActive={favorite.path === activePath}
                reorderable={reorderable}
                onSelect={onSelect}
                targetDescription={favoriteTargetDescription(
                  favorite.path,
                  description.isView,
                  modules,
                  tables ?? []
                )}
              />
            )
          })}
        </SortableContext>
      </nav>
      <DragOverlay dropAnimation={null} modifiers={dragModifiers}>
        {draggedDescription ? (
          <SidebarDragChip
            kind="table"
            icon={iconFor(draggedDescription)}
            label={draggedDescription.name}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
